import { map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { AuthService, provideAuth0 } from '@auth0/auth0-angular';
import { AclService } from '@/core/services/security/acl.service';
import { AccessControlScope } from '@/core/types/security/acl';
import { AuthorityFullData } from '@/core/types/security/authority';
import { Profile } from '@/core/types/security/auth';
import { ProfileService } from '@/core/services/profile.service';
import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';

@Injectable()
export class SecurityService {
    private meChangeSubject = new ReplaySubject<AuthorityFullData>();
    public meChange$ = this.meChangeSubject.asObservable();

    constructor(
        private auth: AuthService,
        private profileService: ProfileService,
        private aclService: AclService
    ) {}

    private _me?: AuthorityFullData;

    public get me() {
        return this._me;
    }

    private _acl?: string[];

    public get acl() {
        return this._acl;
    }

    public getProfile(): Observable<Profile> {
        return this.auth.user$.pipe(
            map((user) => ({
                name: user?.name ?? '',
                avatar: user?.picture ?? '',
                firstName: user?.given_name ?? '',
                lastName: user?.family_name ?? ''
            }))
        );
    }

    public login(returnState: string): Observable<any> {
        return this.auth.loginWithRedirect({
            appState: { target: returnState }
        });
    }

    public logout(): Observable<any> {
        this.auth.logout({
            logoutParams: {
                returnTo: window.location.origin
            }
        });
        return of({});
    }

    isAuthenticated(): Observable<boolean> {
        return this.auth.isAuthenticated$;
    }

    public loadAuthorizedUserInfo() {
        return this.profileService.getMe().pipe(
            tap((val) => {
                console.log('Loaded info');
                this._me = val;
                this._acl = [...new Set(...val.roles.map((r) => r.acl))];
                this.meChangeSubject.next(val);
            })
        );
    }

    public hasEnoughAccess(required: string[], scope: AccessControlScope = 'all'): boolean {
        if (!this._acl || this._acl.length === 0 || required.length === 0) {
            return true;
        }
        this.aclService.validateAcl(required);
        if (this._acl.some((a) => a === AclService.fullAccessControlToken)) {
            return true;
        }
        switch (scope) {
            case 'all':
                return required.every((a) => this._acl?.includes(a));
            case 'any':
                return required.some((a) => this._acl?.includes(a));
            default:
                return false;
        }
    }
}

export const getAuthProvider = () => {
    return provideAuth0({
        ...environment.auth.config,
        authorizationParams: {
            audience: `https://${environment.auth.config.domain}/api/v2/`,
            redirect_uri: window.location.origin
        },
        httpInterceptor: {
            allowedList: environment.auth.secureApiPaths.map((p) => ({
                uri: `${environment.dashboardApiURL}/${p}`
            }))
        }
    });
};
